// @flow
/** @jsx jsx */

import { sample } from 'lodash';
import { useState } from 'react';

import Button from '@zapier/common-components/src/components/Button';
import GridLayout from '@zapier/common-components/src/components/GridLayout';
import Icon from '@zapier/common-components/src/components/Icon';
import Link from '@zapier/common-components/src/components/Link';
import TextInput from '@zapier/common-components/src/components/TextInputWithComponent';
import { StaticImage } from '@zapier/common-components/src/components/StaticAsset';
import { jsx } from '@emotion/core';

import Styles from './NotFound.styles';
import { DEFAULT_ACTIONS, DEFAULT_BACKGROUNDS } from './utils';

import type { NotFoundBackground } from './utils';

type Props = {
  /**
   * Array of action cards that will be displayed underneath the search form.
   */
  actions: Array<{
    alt: string,
    description: string,
    image: string,
    path: string,
    title: string,
  }>,
  /**
   * Array of backgrounds and their descriptions, one will randomly be selected
   * and displayed at the top of the component.
   */
  backgrounds: Array<NotFoundBackground>,
  /**
   * The base error message.
   */
  errorMessage?: string,
  /**
   * Where to send the user back to.
   */
  safetyRoute?: string,
};

const defaultProps = {
  actions: DEFAULT_ACTIONS,
  backgrounds: DEFAULT_BACKGROUNDS,
  errorMessage: '404',
  safetyRoute: '/',
};

function NotFound(props: Props) {
  const [searchString, setSearchString] = useState<string>('');
  const [background] = useState<NotFoundBackground>(sample(props.backgrounds));

  const onSearch = (event: SyntheticInputEvent<HTMLElement>) => {
    setSearchString(event.target.value);
  };

  return (
    <main css={Styles.root}>
      <div css={Styles.heading(background.light, background.image)}>
        <h1 css={Styles.errorCode}>{props.errorMessage}</h1>
        <p css={Styles.headerText}> {background.description} </p>
        <Button color="important" href={props.safetyRoute}>
          Back to Safety
        </Button>
      </div>

      <h2 css={Styles.helpHeading}>We're here to help!</h2>

      <div css={Styles.searchWrapper}>
        <form action="/help/search/" method="get" name="form">
          <GridLayout columns="1fr auto" gap={5}>
            <TextInput
              ariaLabel="Search for what you need help with"
              autoComplete="off"
              name="q"
              onChange={onSearch}
              placeholder="Search for what you need help with..."
              type="text"
              value={searchString}
            />
            <Button css={Styles.searchButton} label="Search" type="submit">
              <Icon name="search" />
            </Button>
          </GridLayout>
        </form>
      </div>

      <div css={Styles.actionWrapper}>
        <GridLayout
          columns={1}
          gap={20}
          propsQueries={{
            tablet: { columns: 3 },
            desktop: { columns: 3 },
          }}
        >
          {props.actions.map((action, index) => (
            <div css={Styles.action} key={`not-found-actions--${index}`}>
              <StaticImage
                alt={action.alt}
                css={Styles.actionImage}
                src={action.image}
              />
              <div css={Styles.actionTitle}>{action.title}</div>
              <div>
                <Link href={action.path}>
                  {action.description}{' '}
                  <Icon css={Styles.arrowRight} name="arrow-right" />
                </Link>
              </div>
            </div>
          ))}
        </GridLayout>
      </div>
    </main>
  );
  // }
}

NotFound.defaultProps = defaultProps;

export default NotFound;
